import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  SmartToyOutlined,
  SettingsOutlined,
  CodeOutlined,
  LinkOutlined,
  VisibilityOutlined,
  ChatBubbleOutlineOutlined,
  Close,
} from "@mui/icons-material";

import QueueSelect from "../QueueSelect";
import ConnectionModalSidebar from "../ConnectionModalSidebar";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";

const PESTANAS = [
  { clave: "chatbot", etiqueta: "Chatbot", icono: <SmartToyOutlined fontSize="small" /> },
  { clave: "config", etiqueta: "Configuración", icono: <SettingsOutlined fontSize="small" /> },
  { clave: "integracion", etiqueta: "Integración", icono: <CodeOutlined fontSize="small" /> },
  { clave: "enlace", etiqueta: "Enlace", icono: <LinkOutlined fontSize="small" /> },
];

const Ajuste = ({ title, descripcion, control, classes }) => (
  <Box className={classes.ajuste}>
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Typography className={classes.ajusteTitulo}>{title}</Typography>
      <Typography className={classes.ajusteDesc}>{descripcion}</Typography>
    </Box>
    {control}
  </Box>
);

const Grupo = ({ children, classes }) => (
  <Typography className={classes.grupo}>{children}</Typography>
);

const Previa = ({ config, saludo, classes }) => {
  const color = config.color || "#3956ff";
  const isLeft = config.position === "left";

  return (
    <Box className={classes.previaCol}>
      <Box className={classes.previaRotulo}>
        <VisibilityOutlined style={{ fontSize: 15 }} />
        Vista previa
      </Box>

      <Box className={classes.previaVentana}>
        <Box style={{ background: color }} className={classes.previaCabecera}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.previaTitulo}>{config.title || " "}</div>
            {config.subtitle && (
              <div className={classes.previaSub}>{config.subtitle}</div>
            )}
          </Box>
          <span style={{ opacity: 0.85 }}>✕</span>
        </Box>

        <Box className={classes.previaCuerpo}>
          {}
          {saludo ? (
            <div className={classes.previaEntrante}>{saludo}</div>
          ) : (
            <div className={classes.previaEntrante}>Tu mensaje aparecerá aquí.</div>
          )}

          <div className={classes.previaSaliente} style={{ background: color }}>
            ¡Hola, buenas tardes!
          </div>
        </Box>

        <Box className={classes.previaPie}>
          <span style={{ flex: 1, opacity: 0.55 }}>Escribe un mensaje</span>
          <span style={{ color }}>➤</span>
        </Box>
      </Box>

      {}
      <Box
        style={{
          display: "flex",
          justifyContent: isLeft ? "flex-start" : "flex-end",
          marginTop: 10,
        }}
      >
        {config.floatingButton !== false ? (
          <span className={classes.previaBurbuja} style={{ background: color }}>
            💬
          </span>
        ) : (
          <span className={classes.previaNota}>
            Sin burbuja: ábrelo con window.ExpresitoChat.abrir()
          </span>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  caja: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1120,
    maxWidth: "95vw",
    height: 620,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    outline: "none",
    overflow: "hidden",
  },
  cabecera: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.25, 2.5, 2),
  },
  insignia: {
    width: 38,
    height: 38,
    borderRadius: 10,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(57,86,255,0.14)",
    color: "#5b6cff",
  },
  title: { fontSize: 16, fontWeight: 600, lineHeight: 1.2 },
  sub: { fontSize: 12.5, color: theme.palette.text.secondary },
  grupo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  ajuste: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    background:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.015)"
        : "rgba(255,255,255,0.022)",
  },
  ajusteTitulo: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 },
  ajusteDesc: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  cuerpo: { display: "flex", flex: 1, minHeight: 0 },
  panel: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2.5, 3, 3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.75),
  },
  ayuda: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 4 },
  codigo: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    background: theme.palette.mode === "light" ? "#f5f4ef" : "#1c1c1a",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    userSelect: "all",
  },
  domains: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  previaCol: {
    flex: "0 0 300px",
    borderLeft: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.022)"
        : "rgba(255,255,255,0.028)",
    padding: theme.spacing(2.5, 2.5),
    overflowY: "auto",
  },
  previaRotulo: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  previaVentana: {
    borderRadius: 14,
    overflow: "hidden",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.10)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.20), 0 2px 6px rgba(0,0,0,0.10)",
    display: "flex",
    flexDirection: "column",
    height: 320,
  },
  previaCabecera: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    color: "#fff",
    padding: "12px 14px",
  },
  previaTitulo: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.25 },
  previaSub: { fontSize: 11.5, opacity: 0.85, marginTop: 2 },
  previaCuerpo: {
    flex: 1,
    background: "#f6f7f9",
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 7,
    overflow: "hidden",
  },
  previaEntrante: {
    alignSelf: "flex-start",
    maxWidth: "82%",
    background: "#fff",
    color: "#1f2430",
    border: "1px solid #e6e6e6",
    borderRadius: 9,
    padding: "7px 9px",
    fontSize: 12,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  previaSaliente: {
    alignSelf: "flex-end",
    maxWidth: "82%",
    color: "#fff",
    borderRadius: 9,
    padding: "7px 9px",
    fontSize: 12,
  },
  previaPie: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 11px",
    background: "#fff",
    borderTop: "1px solid #e6e6e6",
    fontSize: 12,
    color: "#1f2430",
  },
  previaBurbuja: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    color: "#fff",
    boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
  },
  previaNota: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  pie: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.75, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const WebChatModal = ({ open, onClose, conexion, backendUrl, pestanaInicial = "chatbot" }) => {
  const { classes } = useStyles();

  const [pestana, setPestana] = useState(pestanaInicial);
  const [guardando, setGuardando] = useState(false);
  const [token, setToken] = useState("");
  const [nombre, setNombre] = useState("");
  const [saludo, setSaludo] = useState("");
  const [despedida, setDespedida] = useState("");
  const [config, setConfig] = useState({});
  const [colas, setColas] = useState([]);
  const [tolerancia, setTolerancia] = useState(120);
  const [volverAgente, setVolverAgente] = useState(false);
  const [dominioNuevo, setDominioNuevo] = useState("");

  useEffect(() => {
    if (!open || !conexion) return;

    setPestana(pestanaInicial);
    setToken(conexion.token || "");
    setNombre(conexion.name || "");
    setSaludo(conexion.greetingMessage || "");
    setDespedida(conexion.farewellMessage || "");
    setColas((conexion.queues || []).map((q) => q.id));
    setTolerancia(
      conexion.toleranciaReapertura === undefined ? 120 : conexion.toleranciaReapertura
    );
    setVolverAgente(conexion.volverUltimoAgente === true);
    setConfig({
      title: "¡Bienvenido!",
      subtitle: "¿Cómo podemos ayudarte?",
      buttonText: "Chatea con un asesor",
      noticeText: "👋 ¿Necesitas ayuda? Escríbenos.",
      color: "#3956ff",
      position: "right",
      floatingButton: true,
      domains: [],
      hostedPageEnabled: false,
      stillThereMinutes: 2,
      askForDetails: "before",
      countryPrefix: "593",
      ...(conexion.webchatConfig || {}),
    });
  }, [open, conexion, pestanaInicial]);

  const snippet = useMemo(
    () =>
      `<script src="${backendUrl}/public/webchat.js"\n` +
      `        data-token="${token}"></script>`,
    [backendUrl, token]
  );

  const enlace = useMemo(() => `${backendUrl}/chat/${token}`, [backendUrl, token]);

  const cambiar = (clave) => (valor) => setConfig((p) => ({ ...p, [clave]: valor }));

  const agregarDominio = () => {
    const limpio = dominioNuevo.trim();
    if (!limpio) return;

    setConfig((p) => ({ ...p, domains: [...(p.domains || []), limpio] }));
    setDominioNuevo("");
  };

  const guardar = async () => {
    if (guardando) return;
    setGuardando(true);

    const fallos = [];

    try {
      await api.put(`/whatsapp/webchat/${conexion.id}`, { config });
    } catch (err) {
      fallos.push("los ajustes del widget");
      toastError(err);
    }

    try {
      await api.put(`/whatsapp/${conexion.id}`, {
        name: nombre,
        greetingMessage: saludo,
        farewellMessage: despedida,
        queueIds: colas,
        toleranciaReapertura: Number(tolerancia) || 0,
        volverUltimoAgente: volverAgente,
      });
    } catch (err) {
      fallos.push("el chatbot");
      toastError(err);
    }

    setGuardando(false);

    if (fallos.length) {
      return;
    }

    toast.success("Conexión actualizada.");
    onClose(true);
  };

  const rotarToken = async () => {
    try {
      const { data } = await api.put(`/whatsapp/webchat/${conexion.id}/token`);
      setToken(data.token);
      toast.warning("Token cambiado. Vuelve a pegar el script en tu sitio o el chat dejará de funcionar.");
    } catch (err) {
      toastError(err);
    }
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado.");
    } catch {
    }
  };

  if (!conexion) return null;

  return (
    <Modal open={open} onClose={() => onClose(false)} aria-labelledby="webchat-title">
      <Paper className={classes.caja}>
        <Box className={classes.cabecera}>
          <Box className={classes.insignia}>
            <ChatBubbleOutlineOutlined fontSize="small" />
          </Box>

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.title} id="webchat-title">
              Editar Web Chat
            </Typography>
            <Typography className={classes.sub}>{conexion.name}</Typography>
          </Box>

          <IconButton size="small" onClick={() => onClose(false)} aria-label="Cerrar">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box className={classes.cuerpo}>
          <ConnectionModalSidebar
            tabs={PESTANAS.map((p) => ({ value: p.clave, label: p.etiqueta, icon: p.icono }))}
            active={pestana}
            onChange={setPestana}
          />

          <Box className={classes.panel}>
            {pestana === "chatbot" && (
              <>
                <Grupo classes={classes}>Identificación</Grupo>

                <TextField
                  label="Nombre"
                  size="small"
                  fullWidth
                  required
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
                <Grupo classes={classes}>Enrutamiento</Grupo>

                {}
                <QueueSelect
                  selectedQueueIds={colas}
                  onChange={(valores) => setColas(valores)}
                />

                <Grupo classes={classes}>Mensajes automáticos</Grupo>

                <TextField
                  label="Mensaje de saludo"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={saludo}
                  onChange={(e) => setSaludo(e.target.value)}
                  helperText="Se muestra al visitante nada más abrir el chat."
                />
                <TextField
                  label="Mensaje de despedida"
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  value={despedida}
                  onChange={(e) => setDespedida(e.target.value)}
                />

                <Grupo classes={classes}>Identificación</Grupo>

                <Ajuste
                  classes={classes}
                  title="Pedir nombre y teléfono"
                  descripcion={
                    config.askForDetails === "before"
                      ? "Se piden antes de poder escribir. Identificas a todo el que pase, pero quien solo quería un precio puede irse sin preguntar."
                      : config.askForDetails === "after"
                        ? "Se piden tras su primer mensaje. Ya tienes su pregunta aunque no rellene nada, y se va mucha menos gente."
                        : "No se piden. Todos entran como \"Visitante web\" y el asesor no sabe con quién habla."
                  }
                  control={
                    <TextField
                      size="small"
                      select
                      value={config.askForDetails || "before"}
                      onChange={(e) => cambiar("askForDetails")(e.target.value)}
                      style={{ width: 190 }}
                    >
                      <MenuItem value="before">Antes de escribir</MenuItem>
                      <MenuItem value="after">Tras el 1er mensaje</MenuItem>
                      <MenuItem value="never">No pedir</MenuItem>
                    </TextField>
                  }
                />

                <Ajuste
                  classes={classes}
                  title="Código de país"
                  descripcion="Se le añade al teléfono cuando el visitante lo escribe en formato local. Sin esto, quien teclea 0982507094 nunca cruzaría con su contacto de WhatsApp, que se guarda como 593982507094."
                  control={
                    <TextField
                      size="small"
                      value={config.countryPrefix || "593"}
                      onChange={(e) => cambiar("countryPrefix")(e.target.value.replace(/[^0-9]/g, ""))}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">+</InputAdornment>,
                      }}
                      style={{ width: 110 }}
                    />
                  }
                />

                <Grupo classes={classes}>Reapertura</Grupo>

                {}
                <Ajuste
                  classes={classes}
                  title="Período de tolerancia"
                  descripcion={
                    Number(tolerancia) > 0
                      ? `Si el contacto vuelve antes de ${tolerancia} min, sigue la misma conversación y no repite el chatbot. Pasado ese tiempo se abre una nueva y el flujo empieza de cero.`
                      : "Con 0, cada mensaje tras un cierre abre una conversación nueva y el chatbot empieza de cero."
                  }
                  control={
                    <TextField
                      size="small"
                      type="number"
                      value={tolerancia}
                      onChange={(e) => setTolerancia(e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">min</InputAdornment>,
                      }}
                      inputProps={{ min: 0 }}
                      style={{ width: 120 }}
                    />
                  }
                />

                <Ajuste
                  classes={classes}
                  title="Preguntar si sigue ahí"
                  descripcion={
                    Number(config.stillThereMinutes) > 0
                      ? `Si escribes y el visitante no responde en ${config.stillThereMinutes} min, se le pregunta una vez si sigue ahí. Solo una: si ya se preguntó, no se insiste.`
                      : "Desactivado. Nadie preguntará nada aunque el visitante deje de responder."
                  }
                  control={
                    <TextField
                      size="small"
                      type="number"
                      value={config.stillThereMinutes ?? 2}
                      onChange={(e) => cambiar("stillThereMinutes")(e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">min</InputAdornment>,
                      }}
                      inputProps={{ min: 0 }}
                      style={{ width: 120 }}
                    />
                  }
                />

                <Ajuste
                  classes={classes}
                  title="Volver al último agente"
                  descripcion="Si el contacto vuelve dentro del período de tolerancia y el agente está en línea, el chat se reabre directamente con él, sin pasar por el chatbot. Si no está conectado, entra al departamento."
                  control={
                    <Switch
                      checked={volverAgente}
                      onChange={(e) => setVolverAgente(e.target.checked)}
                    />
                  }
                />
              </>
            )}

            {pestana === "config" && (
              <>
                <Grupo classes={classes}>Textos</Grupo>

                <TextField
                  label="Título"
                  size="small"
                  fullWidth
                  value={config.title || ""}
                  onChange={(e) => cambiar("title")(e.target.value)}
                />
                <TextField
                  label="Subtítulo"
                  size="small"
                  fullWidth
                  value={config.subtitle || ""}
                  onChange={(e) => cambiar("subtitle")(e.target.value)}
                />
                <TextField
                  label="Texto de la burbuja"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 40 }}
                  value={config.buttonText || ""}
                  onChange={(e) => cambiar("buttonText")(e.target.value)}
                  helperText="Lo que se lee junto a la burbuja antes de abrirla. Vacío deja solo el icono."
                />
                <TextField
                  label="Globito de saludo"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 90 }}
                  value={config.noticeText ?? ""}
                  onChange={(e) => cambiar("noticeText")(e.target.value)}
                  helperText="Asoma sobre la burbuja a los pocos segundos. Vacío lo apaga."
                />

                <Grupo classes={classes}>Apariencia</Grupo>

                <Box display="flex" style={{ gap: 16 }}>
                  <TextField
                    label="Color"
                    size="small"
                    type="color"
                    value={config.color || "#3956ff"}
                    onChange={(e) => cambiar("color")(e.target.value)}
                    style={{ width: 110 }}
                  />
                  <TextField
                    label="Posición"
                    size="small"
                    select
                    value={config.position || "right"}
                    onChange={(e) => cambiar("position")(e.target.value)}
                    style={{ width: 180 }}
                  >
                    <MenuItem value="right">Derecha</MenuItem>
                    <MenuItem value="left">Izquierda</MenuItem>
                  </TextField>
                </Box>

                <Ajuste
                  classes={classes}
                  title="Botón flotante"
                  descripcion="Muestra la burbuja en la esquina del sitio. Sin ella, tu web tiene que abrir el chat llamando a window.ExpresitoChat.abrir()."
                  control={
                    <Switch
                      checked={config.floatingButton !== false}
                      onChange={(e) => cambiar("floatingButton")(e.target.checked)}
                    />
                  }
                />
              </>
            )}

            {pestana === "integracion" && (
              <>
                <Grupo classes={classes}>Seguridad</Grupo>

                <Box>
                  <Box display="flex" style={{ gap: 8 }}>
                    <TextField
                      label="Dominios permitidos"
                      size="small"
                      fullWidth
                      placeholder="https://mi-sitio-web.com"
                      value={dominioNuevo}
                      onChange={(e) => setDominioNuevo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          agregarDominio();
                        }
                      }}
                    />
                    <Button size="small" onClick={agregarDominio}>
                      Añadir
                    </Button>
                  </Box>

                  <Box className={classes.domains}>
                    {(config.domains || []).map((d) => (
                      <Chip
                        key={d}
                        label={d}
                        size="small"
                        onDelete={() =>
                          setConfig((p) => ({
                            ...p,
                            domains: p.domains.filter((x) => x !== d),
                          }))
                        }
                      />
                    ))}
                  </Box>

                  <Typography className={classes.ayuda}>
                    {(config.domains || []).length === 0
                      ? "Sin domains, el chat se acepta desde cualquier web. Cualquiera que copie tu token podría usarlo."
                      : "Solo estos sitios podrán abrir chat con tu token."}
                  </Typography>
                </Box>

                <Grupo classes={classes}>Instalación</Grupo>

                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                    <Typography style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                      Snippet de código
                    </Typography>
                    <Button size="small" onClick={() => copiar(snippet)}>
                      Copiar
                    </Button>
                    <Button size="small" color="warning" onClick={rotarToken}>
                      Cambiar token
                    </Button>
                  </Box>

                  <Box className={classes.codigo}>{snippet}</Box>

                  <Typography className={classes.ayuda}>
                    Pégalo antes de <code>&lt;/body&gt;</code> en tu sitio.
                  </Typography>
                </Box>
              </>
            )}

            {pestana === "enlace" && (
              <>
                <Ajuste
                  classes={classes}
                  title="Activar enlace del chat"
                  descripcion="Publica una página con este chat, para quien no tiene sitio web. Mientras esté desactivado, el enlace responde 404."
                  control={
                    <Switch
                      checked={config.hostedPageEnabled === true}
                      onChange={(e) => cambiar("hostedPageEnabled")(e.target.checked)}
                    />
                  }
                />

                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                    <Typography style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                      Dirección del enlace
                    </Typography>
                    <Button size="small" onClick={() => copiar(enlace)}>
                      Copiar
                    </Button>
                  </Box>

                  <Box className={classes.codigo}>{enlace}</Box>

                  <Typography className={classes.ayuda}>
                    Úsala en la bio de Instagram, en WhatsApp o en un código QR.
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {}
          {pestana !== "integracion" && (
            <Previa config={config} saludo={saludo} classes={classes} />
          )}
        </Box>

        <Box className={classes.pie}>
          <Typography className={classes.ayuda}>
            Los cambios se aplican en tu web sin volver a tocar el script.
          </Typography>

          <Box display="flex" style={{ gap: 8 }}>
            <Button size="small" color="inherit" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={guardando || !nombre.trim()}
              onClick={guardar}
            >
              {guardando ? "Guardando…" : "Guardar"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default WebChatModal;
